import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Upload,
  message,
} from 'antd';
import {
  CalendarOutlined,
  EditOutlined,
  HistoryOutlined,
  TeamOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiOriginForStaticAssets } from '@/config/apiBaseUrl';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationEmployeeSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';

const CUSTODY_RESTRICTIONS = ['no_pickup', 'no_contact', 'supervised_only', 'court_order', 'other'] as const;
const CUSTODY_BLOCKS_PICKUP = ['no_pickup', 'no_contact'];

const STUDENT_DOCUMENT_TYPES = [
  'birth_certificate',
  'aadhaar',
  'transfer_certificate',
  'caste_certificate',
  'income_certificate',
  'passport_photo',
  'address_proof',
  'medical_fitness',
  'other',
] as const;

const DOC_TYPE_I18N: Record<(typeof STUDENT_DOCUMENT_TYPES)[number], string> = {
  birth_certificate: 'docTypeBirthCertificate',
  aadhaar: 'docTypeAadhaar',
  transfer_certificate: 'docTypeTransferCertificate',
  caste_certificate: 'docTypeCasteCertificate',
  income_certificate: 'docTypeIncomeCertificate',
  passport_photo: 'docTypePassportPhoto',
  address_proof: 'docTypeAddressProof',
  medical_fitness: 'docTypeMedicalFitness',
  other: 'docTypeOther',
};

type StudentDetail = {
  student: {
    id: string;
    admission_no: string;
    first_name: string;
    last_name: string | null;
    status: string;
    campus_id: string;
    user_id: string | null;
    date_of_birth?: string | null;
    gender?: string | null;
    category?: string | null;
    religion?: string | null;
    nationality?: string | null;
    blood_group?: string | null;
    aadhaar_masked?: string | null;
    preferred_language?: string | null;
    photo_url?: string | null;
    quota_code?: string | null;
    quota_name?: string | null;
    roll_no?: string | null;
    board_registration_no?: string | null;
    admission_type?: string | null;
    admission_date?: string | null;
    tc_number?: string | null;
    stream_code?: string | null;
    house_code?: string | null;
    house_name?: string | null;
    phone?: string | null;
    email?: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state_name?: string | null;
    pincode?: string | null;
    father_name?: string | null;
    father_phone?: string | null;
    father_email?: string | null;
    father_occupation?: string | null;
    mother_name?: string | null;
    mother_phone?: string | null;
    mother_email?: string | null;
    mother_occupation?: string | null;
    guardian_name?: string | null;
    guardian_relation?: string | null;
    guardian_phone?: string | null;
    guardian_email?: string | null;
    emergency_contact_name?: string | null;
    emergency_contact_phone?: string | null;
    emergency_contact_relation?: string | null;
    medical_condition?: string | null;
    allergies?: string | null;
    height_cm?: number | string | null;
    weight_kg?: number | string | null;
    pen_id?: string | null;
    apaar_id?: string | null;
    previous_school?: string | null;
    record_version?: number;
  };
  campus?: { id: string; code: string; name: string } | null;
  guardians: Array<{
    id: string;
    link_id?: string;
    first_name: string;
    last_name: string | null;
    relationship_name: string | null;
    user_id: string | null;
    phone?: string | null;
    email?: string | null;
    is_primary?: number | boolean;
    can_pickup?: boolean;
    custody_restriction?: string | null;
    restriction_note?: string | null;
    /** Staff view only: true when the custody restriction removes this guardian's portal access (FO-001). */
    portal_blocked?: boolean;
  }>;
  siblings?: Array<{
    link_id: string;
    id: string;
    admission_no: string;
    first_name: string;
    last_name: string | null;
    status: string;
    relationship_note?: string | null;
  }>;
  teaching_assignments: Array<{
    id: string;
    employee_id: string;
    user_id: string | null;
    employee_code?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  }>;
  current_placement?: {
    enrollment_id: string;
    enrollment_no: string;
    enrollment_status: string;
    effective_on?: string | null;
    session_name?: string | null;
    session_code?: string | null;
    grade_name?: string | null;
    section_name?: string | null;
  } | null;
  documents?: Array<{
    id: string;
    document_type: string;
    title: string;
    verification_status: string;
    expires_on?: string | null;
    storage_ref?: string | null;
  }>;
  timeline?: Array<{
    event_type: string;
    event_at?: string | null;
    summary: string;
    category?: string;
    path?: string | null;
    ref_id?: string | null;
  }>;
  operational_links?: Record<string, { available: boolean; reason?: string; path?: string }>;
};

const TIMELINE_CATEGORIES = [
  'lifecycle',
  'academic',
  'attendance',
  'fees',
  'behaviour',
  'support',
  'ids',
  'services',
] as const;

type ApiError = {
  response?: { status?: number; data?: { message?: string; error?: string } };
  message?: string;
};

function displayName(first?: string | null, last?: string | null) {
  return [first, last].filter(Boolean).join(' ') || '—';
}

function statusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'green';
    case 'inactive':
      return 'default';
    case 'withdrawn':
      return 'orange';
    case 'alumni':
      return 'blue';
    case 'transferred_out':
      return 'purple';
    default:
      return 'default';
  }
}

export default function StudentDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const has = (code: string) => Boolean(user?.permissions?.includes(code));
  const [teacherForm] = Form.useForm<{ employee_id: string; user_id?: string }>();
  const [siblingForm] = Form.useForm<{ sibling_student_id: string; relationship_note?: string }>();
  const [statusForm] = Form.useForm<{ status: string; note?: string }>();
  const [docForm] = Form.useForm<{
    document_type: string;
    title: string;
    verification_status?: string;
    expires_on?: dayjs.Dayjs | null;
  }>();
  const [pendingDocFile, setPendingDocFile] = useState<File | null>(null);
  const [timelineCategory, setTimelineCategory] = useState<string | undefined>();
  const serverOrigin = apiOriginForStaticAssets();

  const query = useQuery({
    queryKey: ['education-student', id],
    queryFn: () => api.get<StudentDetail>(`/education/students/${id}`),
    enabled: Boolean(id),
  });
  const detail = query.data?.data;
  const student = detail?.student;
  const apiError = query.error as ApiError | null;
  const errorMessage =
    apiError?.response?.data?.message ||
    apiError?.response?.data?.error ||
    apiError?.message ||
    null;
  const errorStatus = apiError?.response?.status;
  const photoSrc = student?.photo_url ? `${serverOrigin}${student.photo_url}` : undefined;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['education-student', id] });

  const photoUpload = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('photo', file);
      return api.post<{ photo_url: string }>(`/education/students/${id}/photo`, formData);
    },
    onSuccess: () => {
      message.success(t('photoUploaded'));
      refresh();
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const documentUpload = useMutation({
    mutationFn: async (values: {
      document_type: string;
      title: string;
      verification_status?: string;
      expires_on?: dayjs.Dayjs | null;
    }) => {
      const formData = new FormData();
      formData.append('document_type', values.document_type);
      formData.append('title', values.title);
      formData.append('verification_status', values.verification_status || 'pending');
      if (values.expires_on) formData.append('expires_on', values.expires_on.format('YYYY-MM-DD'));
      if (pendingDocFile) formData.append('file', pendingDocFile);
      return api.post(`/education/students/${id}/documents`, formData);
    },
    onSuccess: () => {
      message.success(t('documentUploaded'));
      setPendingDocFile(null);
      docForm.resetFields();
      refresh();
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const linkGuardian = useMutation({
    mutationFn: (values: {
      first_name: string;
      last_name?: string;
      relationship_name?: string;
      user_id?: string;
      phone?: string;
      email?: string;
      is_primary?: boolean;
    }) => api.post(`/education/students/${id}/guardians`, values),
    onSuccess: () => {
      message.success(t('guardianLinked'));
      refresh();
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const updateGuardianLink = useMutation({
    mutationFn: (input: {
      linkId: string;
      can_pickup?: boolean;
      custody_restriction?: string | null;
      restriction_note?: string | null;
    }) => {
      const { linkId, ...body } = input;
      return api.put(`/education/students/${id}/guardians/${linkId}`, body);
    },
    onSuccess: () => {
      message.success(t('guardianPickupSaved', { defaultValue: 'Pickup and custody saved' }));
      refresh();
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const linkSibling = useMutation({
    mutationFn: (values: { sibling_student_id: string; relationship_note?: string }) =>
      api.post(`/education/students/${id}/siblings`, values),
    onSuccess: () => {
      message.success(t('siblingLinked', { defaultValue: 'Sibling linked' }));
      siblingForm.resetFields();
      refresh();
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const unlinkSibling = useMutation({
    mutationFn: (linkId: string) => api.delete(`/education/students/${id}/siblings/${linkId}`),
    onSuccess: () => {
      message.success(t('siblingUnlinked', { defaultValue: 'Sibling link removed' }));
      refresh();
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const markStatus = useMutation({
    mutationFn: (values: { status: string; note?: string }) => api.post(`/education/students/${id}/status`, values),
    onSuccess: () => {
      message.success(t('studentStatusSaved', { defaultValue: 'Student status updated' }));
      statusForm.resetFields();
      refresh();
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const assignTeacher = useMutation({
    mutationFn: (values: { employee_id: string; user_id?: string }) =>
      api.post(`/education/students/${id}/teaching-assignments`, values),
    onSuccess: () => {
      message.success(t('teacherAssigned'));
      refresh();
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const titleName = student ? displayName(student.first_name, student.last_name) : t('student');

  return (
    <EducationPageChrome
      title={titleName}
      description={student ? `${t('admissionNo')}: ${student.admission_no}` : t('student')}
      tags={
        student ? (
          <>
            <Tag color={statusColor(student.status)}>{student.status}</Tag>
            {student.category ? <Tag>{student.category}</Tag> : null}
            {detail?.current_placement?.grade_name ? (
              <Tag color="blue">
                {detail.current_placement.grade_name}
                {detail.current_placement.section_name
                  ? `–${detail.current_placement.section_name}`
                  : ''}
              </Tag>
            ) : (
              <Tag>{t('noCurrentPlacement')}</Tag>
            )}
          </>
        ) : null
      }
      extra={
        <Space wrap>
          <Button onClick={() => navigate('/education/students')}>{t('backToStudents')}</Button>
          {(has('education.student_twin.view') || has('education.admin')) && student && id ? (
            <Button onClick={() => navigate(`/education/students/${id}/twin`)}>
              {t('studentTwinOpenButton', { defaultValue: 'Digital twin' })}
            </Button>
          ) : null}
          {has('education.student.manage') && student ? (
            <Button icon={<EditOutlined />} type="primary" onClick={() => navigate(`/education/students/${id}/edit`)}>
              {t('editStudent')}
            </Button>
          ) : null}
        </Space>
      }
    >
      {query.isPending ? (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : null}

      {query.isError ? (
        <Card size="small">
          <Empty
            description={
              errorMessage ||
              t('unavailable', { status: errorStatus != null ? String(errorStatus) : '—' })
            }
          >
            <Button onClick={() => refresh()}>{t('retry', { defaultValue: 'Retry' })}</Button>
          </Empty>
        </Card>
      ) : null}

      {!query.isPending && !query.isError && !student ? (
        <Card size="small">
          <Empty description={t('studentNotFound', { defaultValue: 'Student not found' })} />
        </Card>
      ) : null}

      {student ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card size="small" title={t('profileTitle')} loading={query.isLoading}>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} md={6}>
                <Space direction="vertical" align="center" style={{ width: '100%' }}>
                  <Avatar size={112} src={photoSrc} icon={<UserOutlined />} />
                  {has('education.student.manage') ? (
                    <Upload
                      showUploadList={false}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      beforeUpload={(file) => {
                        photoUpload.mutate(file);
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={photoUpload.isPending}>
                        {t('uploadPhoto')}
                      </Button>
                    </Upload>
                  ) : null}
                </Space>
              </Col>
              <Col xs={24} md={18}>
            <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
              <Descriptions.Item label={t('admissionNo')}>{student.admission_no}</Descriptions.Item>
              <Descriptions.Item label={t('name')}>
                {displayName(student.first_name, student.last_name)}
              </Descriptions.Item>
              <Descriptions.Item label={t('status')}>
                <Tag color={statusColor(student.status)}>{student.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('campus')}>
                {detail?.campus
                  ? `${detail.campus.code} — ${detail.campus.name}`
                  : student.campus_id || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('dateOfBirth')}>
                {student.date_of_birth || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('gender')}>{student.gender || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('category')}>{student.category || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('quota', { defaultValue: 'Quota' })}>
                {student.quota_code ? `${student.quota_code} — ${student.quota_name || ''}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('religion', { defaultValue: 'Religion' })}>
                {student.religion || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('nationality', { defaultValue: 'Nationality' })}>
                {student.nationality || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('bloodGroup', { defaultValue: 'Blood group' })}>
                {student.blood_group || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('aadhaar', { defaultValue: 'Aadhaar' })}>
                {student.aadhaar_masked || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('rollNo', { defaultValue: 'Roll no' })}>
                {student.roll_no || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('admissionType', { defaultValue: 'Admission type' })}>
                {student.admission_type || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('admissionDate')}>
                {student.admission_date || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('tcNumber', { defaultValue: 'TC number' })}>
                {student.tc_number || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('house', { defaultValue: 'House' })}>
                {student.house_code ? `${student.house_code} — ${student.house_name || ''}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('stream', { defaultValue: 'Stream' })}>
                {student.stream_code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('penId')}>{student.pen_id || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('apaarId')}>{student.apaar_id || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('boardId', { defaultValue: 'Board registration' })}>
                {student.board_registration_no || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('phone')}>{student.phone || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('email')}>{student.email || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('preferredLanguage', { defaultValue: 'Preferred language' })}>
                {student.preferred_language || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('address', { defaultValue: 'Address' })} span={3}>
                {[student.address_line1, student.address_line2, student.city, student.state_name, student.pincode]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('previousSchool')} span={2}>
                {student.previous_school || '—'}
              </Descriptions.Item>
            </Descriptions>
              </Col>
            </Row>
          </Card>

          <Card size="small" title={t('familyTab')}>
            <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
              <Descriptions.Item label={t('fatherName', { defaultValue: 'Father' })}>
                {student.father_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('fatherPhone', { defaultValue: 'Father phone' })}>
                {student.father_phone || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('fatherOccupation', { defaultValue: 'Father occupation' })}>
                {student.father_occupation || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('motherName', { defaultValue: 'Mother' })}>
                {student.mother_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('motherPhone', { defaultValue: 'Mother phone' })}>
                {student.mother_phone || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('motherOccupation', { defaultValue: 'Mother occupation' })}>
                {student.mother_occupation || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('guardianName', { defaultValue: 'Guardian' })}>
                {student.guardian_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('guardianRelation', { defaultValue: 'Relation' })}>
                {student.guardian_relation || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('guardianPhone', { defaultValue: 'Guardian phone' })}>
                {student.guardian_phone || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('emergencyName', { defaultValue: 'Emergency contact' })}>
                {student.emergency_contact_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('emergencyPhone', { defaultValue: 'Emergency phone' })}>
                {student.emergency_contact_phone || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('emergencyRelation', { defaultValue: 'Emergency relation' })}>
                {student.emergency_contact_relation || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {(has('education.health.view') || has('education.health.edit') || has('education.student.manage')) && (
            <Card size="small" title={t('healthTab', { defaultValue: 'Health summary' })}>
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
                <Descriptions.Item label={t('medicalCondition', { defaultValue: 'Medical condition' })} span={2}>
                  {student.medical_condition || '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('allergies', { defaultValue: 'Allergies' })} span={2}>
                  {student.allergies || '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('heightCm', { defaultValue: 'Height (cm)' })}>
                  {student.height_cm ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('weightKg', { defaultValue: 'Weight (kg)' })}>
                  {student.weight_kg ?? '—'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          <Card size="small" title={t('currentPlacement')}>
            {detail?.current_placement ? (
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
                <Descriptions.Item label={t('enrollmentNo')}>
                  {detail.current_placement.enrollment_no}
                </Descriptions.Item>
                <Descriptions.Item label={t('session')}>
                  {detail.current_placement.session_name ||
                    detail.current_placement.session_code ||
                    '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('grade')}>
                  {detail.current_placement.grade_name || '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('section')}>
                  {detail.current_placement.section_name || '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('status')}>
                  {detail.current_placement.enrollment_status}
                </Descriptions.Item>
                <Descriptions.Item label={t('effectiveOn')}>
                  {detail.current_placement.effective_on || '—'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('noCurrentPlacement')}>
                {has('education.enrollment.manage') ? (
                  <Button type="primary" onClick={() => navigate(`/education/enrollments/new?student_id=${id}`)}>
                    {t('newEnrollment')}
                  </Button>
                ) : null}
              </Empty>
            )}
          </Card>

          <Card size="small" title={t('operationalLinks')}>
            <Space wrap>
              {has('education.enrollment.view') || has('education.enrollment.manage') ? (
                <Button
                  icon={<HistoryOutlined />}
                  onClick={() => navigate(`/education/students/${id}/academic-history`)}
                >
                  {t('academicHistory')}
                </Button>
              ) : null}
              {has('education.attendance.view') || has('education.attendance.mark') ? (
                <Button
                  icon={<CalendarOutlined />}
                  onClick={() => navigate(`/education/students/${id}/attendance`)}
                >
                  {t('attendanceHistory')}
                </Button>
              ) : null}
              {has('education.enrollment.manage') ? (
                <Button onClick={() => navigate(`/education/enrollments/new?student_id=${id}`)}>
                  {t('newEnrollment')}
                </Button>
              ) : null}
              {has('education.transfer_certificate.issue') ? (
                <Button onClick={() => navigate('/education/transfer-certificates')}>
                  {t('transferCertificate')}
                </Button>
              ) : null}
              {has('education.health.view') || has('education.health.edit') ? (
                <Button onClick={() => navigate(`/education/health?student_id=${id}`)}>
                  {t('healthTitle')}
                </Button>
              ) : null}
              {detail?.operational_links?.fees?.available &&
              (has('education.fee.view') ||
                has('education.fee.demand') ||
                has('education.fee.collect') ||
                has('education.admin')) ? (
                <Button
                  onClick={() =>
                    navigate(detail.operational_links?.fees?.path || `/education/fees/students/${id}`)
                  }
                >
                  {t('studentFees')}
                </Button>
              ) : null}
              {detail?.operational_links?.transport?.available &&
              (has('education.transport.view') ||
                has('education.transport.manage') ||
                has('education.admin')) ? (
                <Button
                  onClick={() =>
                    navigate(
                      detail.operational_links?.transport?.path ||
                        `/education/transport/enrollments/new?student_id=${id}`
                    )
                  }
                >
                  {t('transport')}
                </Button>
              ) : null}
              {detail?.operational_links?.library?.available &&
              (has('education.library.view') ||
                has('education.library.circulate') ||
                has('education.admin')) ? (
                <Button
                  onClick={() =>
                    navigate(
                      detail.operational_links?.library?.path ||
                        `/education/library/loans?student_id=${id}`
                    )
                  }
                >
                  {t('library')}
                </Button>
              ) : null}
              {detail?.operational_links?.activities?.available &&
              (has('education.activity.view') ||
                has('education.activity.manage') ||
                has('education.admin')) ? (
                <Button
                  onClick={() =>
                    navigate(
                      detail.operational_links?.activities?.path ||
                        `/education/activities/memberships?student_id=${id}`
                    )
                  }
                >
                  {t('activities')}
                </Button>
              ) : null}
              {detail?.operational_links?.attendance?.available &&
              (has('education.attendance.view') ||
                has('education.attendance.mark') ||
                has('education.admin')) ? (
                <Button
                  onClick={() =>
                    navigate(
                      detail.operational_links?.attendance?.path ||
                        `/education/students/${id}/attendance`
                    )
                  }
                >
                  {t('attendanceTitle', { defaultValue: 'Attendance' })}
                </Button>
              ) : null}
              {detail?.operational_links?.print_progress?.available ? (
                <Button onClick={() => navigate(detail.operational_links?.print_progress?.path || '/education/print')}>
                  {t('printHub', { defaultValue: 'Print pack' })}
                </Button>
              ) : null}
            </Space>
            <Descriptions size="small" column={1} style={{ marginTop: 12 }}>
              {Object.entries(detail?.operational_links || {}).map(([key, link]) =>
                link.available ? null : (
                  <Descriptions.Item key={key} label={key}>
                    {link.reason || t('moduleNotInstalled', { defaultValue: 'Module not installed yet' })}
                  </Descriptions.Item>
                )
              )}
            </Descriptions>
          </Card>

          <Card size="small" title={t('documentVault')}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={detail?.documents ?? []}
              locale={{ emptyText: t('documentsEmpty') }}
              columns={[
                {
                  title: t('documentType'),
                  dataIndex: 'document_type',
                  width: 180,
                  render: (value: string) =>
                    t(DOC_TYPE_I18N[value as keyof typeof DOC_TYPE_I18N] || 'docTypeOther', {
                      defaultValue: value,
                    }),
                },
                { title: t('documentTitle'), dataIndex: 'title' },
                {
                  title: t('verification'),
                  dataIndex: 'verification_status',
                  width: 130,
                  render: (v: string) => <Tag>{v}</Tag>,
                },
                { title: t('expiresOn'), dataIndex: 'expires_on', width: 120 },
                {
                  title: t('documentFile'),
                  dataIndex: 'storage_ref',
                  width: 120,
                  render: (ref: string | null | undefined) =>
                    ref ? (
                      <a href={`${serverOrigin}${ref}`} target="_blank" rel="noreferrer">
                        {t('documentOpen')}
                      </a>
                    ) : (
                      t('documentNoFile')
                    ),
                },
              ]}
            />
            {has('education.student.manage') ? (
              <Form
                form={docForm}
                layout="vertical"
                style={{ marginTop: 16 }}
                initialValues={{ verification_status: 'pending' }}
                onFinish={(values) => documentUpload.mutate(values)}
              >
                <Row gutter={12}>
                  <Col xs={24} md={6}>
                    <Form.Item name="document_type" label={t('documentType')} rules={[{ required: true }]}>
                      <Select
                        options={STUDENT_DOCUMENT_TYPES.map((type) => ({
                          value: type,
                          label: t(DOC_TYPE_I18N[type]),
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="title" label={t('documentTitle')} rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item name="verification_status" label={t('verification')}>
                      <Select
                        options={[
                          { value: 'pending', label: t('verificationPending') },
                          { value: 'verified', label: t('verificationVerified') },
                          { value: 'rejected', label: t('verificationRejected') },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item name="expires_on" label={t('expiresOn')}>
                      <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item label={t('documentFile')}>
                      <Upload
                        maxCount={1}
                        accept=".pdf,image/jpeg,image/png,image/gif,image/webp"
                        beforeUpload={(file) => {
                          setPendingDocFile(file);
                          return false;
                        }}
                        onRemove={() => setPendingDocFile(null)}
                        fileList={
                          pendingDocFile
                            ? [{ uid: 'pending-doc', name: pendingDocFile.name, status: 'done' }]
                            : []
                        }
                      >
                        <Button icon={<UploadOutlined />}>{t('documentUpload')}</Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                </Row>
                <Button type="primary" htmlType="submit" loading={documentUpload.isPending}>
                  {t('addDocument')}
                </Button>
              </Form>
            ) : null}
          </Card>

          <Card size="small" title={t('studentTimeline', { defaultValue: 'Student timeline' })}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Select
                allowClear
                placeholder={t('timelineCategoryFilter', { defaultValue: 'Filter by category' })}
                style={{ minWidth: 220 }}
                value={timelineCategory}
                options={TIMELINE_CATEGORIES.map((c) => ({
                  value: c,
                  label: t(`timelineCategory.${c}`, { defaultValue: c }),
                }))}
                onChange={(value) => setTimelineCategory(value || undefined)}
              />
              <Table
                rowKey={(_r, index) => String(index)}
                size="small"
                pagination={{ pageSize: 12 }}
                dataSource={(detail?.timeline ?? []).filter((row) =>
                  timelineCategory ? row.category === timelineCategory : true
                )}
                locale={{ emptyText: t('timelineEmpty', { defaultValue: 'No lifecycle events yet' }) }}
                columns={[
                  { title: t('when', { defaultValue: 'When' }), dataIndex: 'event_at', width: 170 },
                  {
                    title: t('category', { defaultValue: 'Category' }),
                    dataIndex: 'category',
                    width: 110,
                    render: (value?: string) => (value ? <Tag>{value}</Tag> : '—'),
                  },
                  { title: t('type', { defaultValue: 'Type' }), dataIndex: 'event_type', width: 160 },
                  { title: t('summary', { defaultValue: 'Summary' }), dataIndex: 'summary' },
                  {
                    title: t('actions'),
                    width: 90,
                    render: (_: unknown, row: NonNullable<StudentDetail['timeline']>[number]) =>
                      row.path ? (
                        <Button size="small" type="link" onClick={() => navigate(row.path!)}>
                          {t('open')}
                        </Button>
                      ) : (
                        '—'
                      ),
                  },
                ]}
              />
            </Space>
          </Card>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={14}>
              <Card size="small" title={t('guardians')}>
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={detail?.guardians ?? []}
                  locale={{ emptyText: t('guardiansEmpty') }}
                  columns={[
                    {
                      title: t('name'),
                      render: (_v, row) => (
                        <Space>
                          {displayName(row.first_name, row.last_name)}
                          {row.is_primary ? <Tag color="blue">{t('primaryGuardian')}</Tag> : null}
                          {row.portal_blocked ? (
                            <Tag color="red" data-testid="guardian-portal-blocked">
                              {t('guardianPortalBlocked', { defaultValue: 'Portal blocked' })}
                            </Tag>
                          ) : null}
                        </Space>
                      ),
                    },
                    { title: t('relationship'), dataIndex: 'relationship_name', width: 120 },
                    { title: t('phone'), dataIndex: 'phone', width: 130, render: (v) => v || '—' },
                    { title: t('email'), dataIndex: 'email', ellipsis: true, render: (v) => v || '—' },
                    {
                      title: t('guardianPickup', { defaultValue: 'Pickup' }),
                      width: 110,
                      render: (_v, row) => {
                        const allowed = row.can_pickup !== false;
                        if (!has('education.guardian.manage') || !row.link_id) {
                          return <Tag color={allowed ? 'green' : 'red'}>{allowed ? t('allowed', { defaultValue: 'Allowed' }) : t('blocked', { defaultValue: 'Blocked' })}</Tag>;
                        }
                        return (
                          <Switch
                            size="small"
                            checked={allowed}
                            loading={updateGuardianLink.isPending}
                            onChange={(checked) => updateGuardianLink.mutate({ linkId: row.link_id!, can_pickup: checked })}
                          />
                        );
                      },
                    },
                    ...(detail?.guardians?.some((row) => row.custody_restriction !== undefined)
                      ? [
                          {
                            title: t('custodyRestriction', { defaultValue: 'Custody restriction' }),
                            width: 190,
                            render: (_v: unknown, row: NonNullable<StudentDetail['guardians']>[number]) =>
                              has('education.guardian.manage') && row.link_id ? (
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                  <Select
                                    size="small"
                                    allowClear
                                    style={{ width: '100%' }}
                                    value={row.custody_restriction || undefined}
                                    placeholder={t('none', { defaultValue: 'None' })}
                                    options={CUSTODY_RESTRICTIONS.map((value) => ({ value, label: value }))}
                                    onChange={(value) =>
                                      updateGuardianLink.mutate({
                                        linkId: row.link_id!,
                                        custody_restriction: value || null,
                                        ...(value && CUSTODY_BLOCKS_PICKUP.includes(value) ? { can_pickup: false } : {}),
                                      })
                                    }
                                  />
                                  <Input
                                    size="small"
                                    maxLength={500}
                                    defaultValue={row.restriction_note || ''}
                                    placeholder={t('restrictionNote', { defaultValue: 'Note' })}
                                    onBlur={(event) => {
                                      const next = event.target.value.trim();
                                      if (next !== (row.restriction_note || '')) {
                                        updateGuardianLink.mutate({ linkId: row.link_id!, restriction_note: next || null });
                                      }
                                    }}
                                  />
                                </Space>
                              ) : (
                                row.custody_restriction || '—'
                              ),
                          },
                        ]
                      : []),
                  ]}
                />
                {has('education.guardian.manage') ? (
                  <Form
                    layout="vertical"
                    onFinish={(values) => linkGuardian.mutate(values)}
                    style={{ marginTop: 16 }}
                  >
                    <Row gutter={12}>
                      <Col xs={24} md={8}>
                        <Form.Item name="first_name" label={t('firstName')} rules={[{ required: true }]}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="last_name" label={t('lastName')}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="relationship_name" label={t('relationship')}>
                          <Input placeholder={t('relationshipHint')} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="phone" label={t('phone')}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="email" label={t('email')}>
                          <Input type="email" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="user_id" label={t('userId')}>
                          <Input placeholder={t('guardianUserHint')} />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item name="is_primary" valuePropName="checked">
                          <Checkbox>{t('primaryGuardian')}</Checkbox>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Button type="primary" htmlType="submit" loading={linkGuardian.isPending}>
                      {t('linkGuardian')}
                    </Button>
                  </Form>
                ) : null}
              </Card>
            </Col>
            <Col xs={24} lg={10}>
              <Card
                size="small"
                title={
                  <Space>
                    <TeamOutlined />
                    {t('teachers')}
                  </Space>
                }
              >
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={detail?.teaching_assignments ?? []}
                  locale={{ emptyText: t('teachersEmpty') }}
                  columns={[
                    {
                      title: t('name'),
                      render: (_v, row) => displayName(row.first_name, row.last_name),
                    },
                    { title: t('employeeCode'), dataIndex: 'employee_code', width: 110 },
                    { title: t('employeeId'), dataIndex: 'employee_id', ellipsis: true },
                  ]}
                />
                {has('education.staff.manage') ? (
                  <Form
                    form={teacherForm}
                    layout="vertical"
                    onFinish={(values) => assignTeacher.mutate(values)}
                    style={{ marginTop: 16 }}
                  >
                    <Form.Item name="employee_id" label={t('employee')} rules={[{ required: true }]}>
                      <EducationEmployeeSelect
                        onEmployeePicked={(row) => {
                          teacherForm.setFieldValue('user_id', row?.user_id || undefined);
                        }}
                      />
                    </Form.Item>
                    <Form.Item name="user_id" hidden>
                      <Input />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={assignTeacher.isPending}>
                      {t('assignTeacher')}
                    </Button>
                  </Form>
                ) : null}
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={14}>
              <Card size="small" title={t('siblings', { defaultValue: 'Siblings' })}>
                <Table
                  rowKey="link_id"
                  size="small"
                  pagination={false}
                  dataSource={detail?.siblings ?? []}
                  locale={{ emptyText: t('siblingsEmpty', { defaultValue: 'No siblings linked' }) }}
                  columns={[
                    {
                      title: t('name'),
                      render: (_v, row) => (
                        <Button type="link" size="small" onClick={() => navigate(`/education/students/${row.id}`)}>
                          {displayName(row.first_name, row.last_name)}
                        </Button>
                      ),
                    },
                    { title: t('admissionNo'), dataIndex: 'admission_no', width: 120 },
                    { title: t('status'), dataIndex: 'status', width: 100, render: (v: string) => <Tag>{v}</Tag> },
                    { title: t('note', { defaultValue: 'Note' }), dataIndex: 'relationship_note', ellipsis: true, render: (v) => v || '—' },
                    ...(has('education.student.manage')
                      ? [
                          {
                            title: '',
                            width: 90,
                            render: (_v: unknown, row: NonNullable<StudentDetail['siblings']>[number]) => (
                              <Popconfirm
                                title={t('siblingUnlinkConfirm', { defaultValue: 'Remove this sibling link?' })}
                                onConfirm={() => unlinkSibling.mutate(row.link_id)}
                              >
                                <Button type="link" danger size="small">
                                  {t('remove', { defaultValue: 'Remove' })}
                                </Button>
                              </Popconfirm>
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
                {has('education.student.manage') ? (
                  <Form
                    form={siblingForm}
                    layout="vertical"
                    onFinish={(values) => linkSibling.mutate(values)}
                    style={{ marginTop: 16 }}
                  >
                    <Row gutter={12}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="sibling_student_id"
                          label={t('student')}
                          rules={[{ required: true }]}
                        >
                          <EducationStudentSelect />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="relationship_note" label={t('note', { defaultValue: 'Note' })}>
                          <Input maxLength={200} placeholder={t('siblingNoteHint', { defaultValue: 'e.g. half-sibling, cousin raised together' })} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Button type="primary" htmlType="submit" loading={linkSibling.isPending}>
                      {t('linkSibling', { defaultValue: 'Link sibling' })}
                    </Button>
                  </Form>
                ) : null}
              </Card>
            </Col>
            {has('education.student.manage') && student && ['active', 'withdrawn', 'completed', 'graduated'].includes(student.status) ? (
              <Col xs={24} lg={10}>
                <Card size="small" title={t('studentLifecycleStatus', { defaultValue: 'Graduation and alumni' })}>
                  <Form form={statusForm} layout="vertical" onFinish={(values) => markStatus.mutate(values)}>
                    <Form.Item name="status" label={t('status')} rules={[{ required: true }]}>
                      <Select
                        options={[
                          ...(student.status === 'graduated' ? [] : [{ value: 'graduated', label: t('statusGraduated', { defaultValue: 'Graduated' }) }]),
                          { value: 'alumni', label: t('statusAlumni', { defaultValue: 'Alumni' }) },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item name="note" label={t('note', { defaultValue: 'Note' })}>
                      <Input maxLength={300} />
                    </Form.Item>
                    <Popconfirm
                      title={t('studentStatusConfirm', { defaultValue: 'Update the student status? Enrollments are not changed.' })}
                      onConfirm={() => statusForm.submit()}
                    >
                      <Button type="primary" loading={markStatus.isPending}>
                        {t('updateStatus', { defaultValue: 'Update status' })}
                      </Button>
                    </Popconfirm>
                  </Form>
                </Card>
              </Col>
            ) : null}
          </Row>
        </Space>
      ) : null}
    </EducationPageChrome>
  );
}
